/**
 * Telegram **account** mode (sources.md "telegram"): one chat of the user's own account, read through the
 * device's `TelegramGateway` (MTProto in the platform layer). The session never leaves the device.
 *
 *   fetch     gateway.history(peerId, cursor.lastId, 100), ascending
 *   ids       `<peerId>:<message id>`; an album (groupedId) is ONE item under its lowest id
 *   body      the gateway's entity HTML; a text-less media post gets `[Photo]` etc.; no title
 *   author    the sender's name in groups, the chat title in channels (what the gateway reports)
 *   skipped   service messages (no text and no media) — the cursor still moves past them
 *   cursor    `{ lastId }`: the highest message id handed to ingest (or skipped)
 *   errors    FLOOD_WAIT arrives from the gateway as rate_limited with `retryAfterMs`
 */

import type { MediaItem, RawItem, SourceFetchContext, SourceFetchResult, SourceResolution, SourceRuntime, TelegramGateway, TelegramMessage } from '../../contracts/sources.js';
import type { TelegramConfig } from '../../contracts/sources.js';
import { DigesterError } from '../../kernel/errors.js';
import { escapeHtml, invalidInput } from '../http.js';

/** sources.md: `gateway.history(peerId, cursor.lastId, 100)`. */
export const HISTORY_LIMIT = 100;
/** How many dialogs resolve looks through for the chat's title. */
const RESOLVE_DIALOGS = 500;

const PEER_ID = /^-?\d{1,20}$/;

export function requireGateway(runtime: SourceRuntime): TelegramGateway {
  if (!runtime.telegram) {
    throw new DigesterError('unsupported', 'Telegram account mode is not available here: this build has no Telegram API id.');
  }
  return runtime.telegram;
}

export async function resolveAccount(address: string, config: Partial<TelegramConfig> | undefined, runtime: SourceRuntime): Promise<SourceResolution<'telegram'>> {
  const gateway = requireGateway(runtime);
  const peerId = (config?.peerId ?? address).trim();
  if (!PEER_ID.test(peerId)) throw invalidInput('Pick a chat from your Telegram account.', 'config.peerId');
  const dialog = (await gateway.listDialogs(RESOLVE_DIALOGS)).find((d) => d.peerId === peerId);
  const peerType = dialog?.peerType ?? config?.peerType ?? (peerId.startsWith('-100') ? 'channel' : peerId.startsWith('-') ? 'group' : 'user');
  const username = dialog?.username ?? config?.username;
  const resolved: TelegramConfig = { kind: 'telegram', mode: 'account', peerId, peerType };
  if (username) resolved.username = username;
  return {
    kind: 'telegram',
    locator: `telegram:peer:${peerId}`,
    name: dialog?.title ?? (username ? `@${username}` : `Telegram ${peerId}`),
    config: resolved,
    requiredSecrets: ['telegram:session'],
  };
}

const MEDIA_LABEL: Record<MediaItem['type'], string> = { image: 'Photo', video: 'Video', audio: 'Audio', link: 'Link' };

/** A message with no text and no media is a service message (join, pin, title change…). */
export function isServiceMessage(m: TelegramMessage): boolean {
  return m.html.trim() === '' && m.media.length === 0;
}

function mediaKinds(media: readonly MediaItem[]): string[] {
  return media.map((m) => (m.type === 'image' ? 'photo' : m.type));
}

/** Messages (ascending) → items: service messages dropped, albums merged under their lowest id. */
export function messagesToItems(peerId: string, messages: readonly TelegramMessage[]): Array<{ item: RawItem; maxId: number }> {
  const groups: TelegramMessage[][] = [];
  const byAlbum = new Map<string, TelegramMessage[]>();
  for (const m of [...messages].sort((a, b) => a.id - b.id)) {
    if (isServiceMessage(m)) continue;
    if (m.groupedId) {
      const album = byAlbum.get(m.groupedId);
      if (album) {
        album.push(m);
        continue;
      }
      const fresh = [m];
      byAlbum.set(m.groupedId, fresh);
      groups.push(fresh);
    } else {
      groups.push([m]);
    }
  }
  return groups.map((group) => {
    const first = group[0] as TelegramMessage;
    const media = group.flatMap((m) => m.media);
    const html = group.map((m) => m.html.trim()).filter((h) => h !== '').join('');
    const item: RawItem = {
      externalId: `${peerId}:${first.id}`,
      publishedAt: first.date,
      threadKey: peerId,
      extra: { media: mediaKinds(media), ...(group.length > 1 ? { albumIds: group.map((m) => m.id) } : {}), ...(first.replyToId !== null ? { replyToId: first.replyToId } : {}) },
    };
    if (html) item.html = html;
    else item.html = `<p>${escapeHtml(`[${MEDIA_LABEL[media[0]?.type ?? 'link']}]`)}</p>`;
    const author = group.find((m) => m.authorName)?.authorName;
    if (author) item.author = author;
    const url = first.url ?? group.find((m) => m.url)?.url;
    if (url) item.url = url;
    if (media.length > 0) item.media = media;
    const image = media.find((m) => m.type === 'image');
    if (image) item.imageUrl = image.url;
    return { item, maxId: group.reduce((max, m) => Math.max(max, m.id), 0) };
  });
}

export async function fetchAccount(ctx: SourceFetchContext<'telegram'>): Promise<SourceFetchResult> {
  const gateway = requireGateway(ctx.runtime);
  const peerId = ctx.source.config.peerId;
  if (!peerId) throw new DigesterError('parse_failed', 'This Telegram source has no chat id.');
  const lastId = typeof ctx.cursor?.lastId === 'number' ? ctx.cursor.lastId : 0;
  let messages: TelegramMessage[];
  try {
    messages = await gateway.history(peerId, lastId, HISTORY_LIMIT);
  } catch (error) {
    // A rate limit without a wait gets the engine's default (fetch.default_retry_after_minutes).
    throw error;
  }
  let fresh = messages.filter((m) => m.id > lastId).sort((a, b) => a.id - b.id);
  // A full page may end inside an album: leave that album for the next fetch so it stays one item.
  const tail = fresh[fresh.length - 1];
  if (messages.length >= HISTORY_LIMIT && tail?.groupedId) {
    const cut = fresh.filter((m) => m.groupedId !== tail.groupedId);
    if (cut.length > 0) fresh = cut;
  }
  const highestSeen = fresh.reduce((max, m) => Math.max(max, m.id), lastId);
  let entries = messagesToItems(peerId, fresh);
  let nextLastId = highestSeen;
  if (entries.length > ctx.maxItems) {
    if (ctx.initial) {
      // First fetch: the newest maxItems; everything older is behind the cursor for good.
      entries = entries.slice(entries.length - ctx.maxItems);
    } else {
      // Later fetches: the oldest maxItems, and the cursor stops after them so the rest come next time.
      entries = entries.slice(0, ctx.maxItems);
      nextLastId = entries.reduce((max, e) => Math.max(max, e.maxId), lastId);
    }
  }
  return { items: entries.map((e) => e.item), cursor: { ...(ctx.cursor ?? {}), lastId: nextLastId } };
}
